import { useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import { ExchangeScenarioEnum } from "@b2bportal/air-exchange-api";
import { FlightPostBookingSelectors } from "@b2bportal/core-flights";
import { useModuleBEM } from "@b2bportal/core-themes";
import { FlightExchangeModalState } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  ChangeFlightEligibilityContactAirline,
  ChangeFlightEligibilityLoadFailed,
  ChangeFlightEligibilityLoading,
  ChangeFlightEligibilityVoidable,
  FlightExchangeIneligible,
  useChangeFlightEligibility,
} from "@tenant/flights";
import { Button, ButtonTypeEnum, Card } from "@tenant/ui";
import clsx from "clsx";
import styles from "./ChangeFlightEligibilityModal.module.scss";
import { ContactSupportCard } from "./ContactSupportCard";
import { useExperiment } from "@/modules/experiments/src";
import { MyTripPageHeader } from "@/overrides/apac/apac-ui/components/MyTripPageHeader/MyTripPageHeader";
import { FlightDetailsPanel } from "@/overrides/flights/components/FlightItineraryDetailPage/components/FlightDetailsPanel";

export interface IChangeFlightEligibilityModalContentProps {
  className?: string;
  /** Whether exchange functionality is enabled. When false, shows contact support message instead of policy details. */
  isEnabled?: boolean;
  itinerary: BookedFlightItineraryWithDepartureTime;
  /** Callback to navigate back to trip details */
  onBack?: () => void;
}

export const ChangeFlightEligibilityModalContent = (
  props: IChangeFlightEligibilityModalContentProps
) => {
  const { className, isEnabled = false, itinerary, onBack } = props;
  const ComponentName = "ChangeFlightEligibilityModal";
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const navigateToCancellation = useCallback(
    () => navigate("cancel"),
    [navigate]
  );
  const { isExperimentVariant } = useExperiment();
  const { context, copy, handlers } = useChangeFlightEligibility(
    isEnabled,
    itinerary,
    true
  );
  const [block, cn] = useModuleBEM(styles, ComponentName);
  const airports = useSelector(FlightPostBookingSelectors.getAirports);
  const airlines = useSelector(FlightPostBookingSelectors.getAirlines);
  const maybeContactAirlineScenario = handlers.getContactAirlineScenario(
    context.exchangePolicy?.exchangeScenario
  );

  const showRightColumn =
    context.modalState !== FlightExchangeModalState.Loading;

  let policyContent = null;

  if (!isEnabled) {
    policyContent = <ContactSupportCard />;
  } else if (
    context.exchangePolicy == null &&
    context.modalState !== FlightExchangeModalState.LoadFailed
  ) {
    policyContent = (
      <ChangeFlightEligibilityLoading
        classes={{
          modalContent: cn("modal-content", ["loading"]),
          loadingText: cn("loading-text"),
        }}
        copy={copy}
      />
    );
  } else {
    // Switch on modal state for all other cases
    switch (context.modalState) {
      case FlightExchangeModalState.Loading:
        policyContent = (
          <ChangeFlightEligibilityLoading
            classes={{
              modalContent: cn("modal-content", ["loading"]),
              loadingText: cn("loading-text"),
            }}
            copy={copy}
          />
        );
        break;

      case FlightExchangeModalState.ContactSupport:
        policyContent = <ContactSupportCard />;
        break;

      case FlightExchangeModalState.ContactAirline:
        if (maybeContactAirlineScenario != null) {
          policyContent = (
            <Card className={cn("policy-card")}>
              <ChangeFlightEligibilityContactAirline
                classes={{
                  contentActions: cn("content-actions"),
                  contentBody: cn("content-body"),
                  contentHeader: cn("content-header"),
                  contentTitle: cn("content-title"),
                  modalContent: cn("modal-content", ["contact-airline"]),
                  visitAirlineBtn: cn("visit-airline-btn"),
                }}
                copy={copy}
                policy={maybeContactAirlineScenario}
                policyCopy={context.policyCopy}
              />
            </Card>
          );
        } else {
          // Fallback if no contact airline scenario
          policyContent = <ContactSupportCard />;
        }
        break;

      case FlightExchangeModalState.IneligibleForExchange:
        policyContent = (
          <Card className={cn("policy-card")}>
            <FlightExchangeIneligible
              classes={{
                closeBtn: cn("close-btn"),
                contentActions: cn("content-actions"),
                contentBody: cn("content-body"),
                contentHeader: cn("content-header"),
                contentTitle: cn("content-title"),
                modalContent: cn("modal-content", ["ineligible"]),
              }}
              copy={copy}
            />
          </Card>
        );
        break;

      case FlightExchangeModalState.LoadFailed:
        policyContent = (
          <Card className={cn("policy-card")}>
            <ChangeFlightEligibilityLoadFailed
              classes={{
                contactSupportBtn: cn("contact-support-btn"),
                contentActions: cn("content-actions"),
                contentBody: cn("content-body"),
                contentHeader: cn("content-header"),
                contentTitle: cn("content-title"),
                modalContent: cn("modal-content", ["load-failed"]),
                tryAgainBtn: cn("try-again-btn"),
              }}
              copy={copy}
              onContactSupport={handlers.goToContactSupport}
              onTryAgain={handlers.fetchExchangePolicy}
            />
          </Card>
        );
        break;

      case FlightExchangeModalState.TicketedVoidable:
        if (
          context.exchangePolicy?.exchangeScenario?.ExchangeScenario ===
          ExchangeScenarioEnum.ContactCustomerService
        ) {
          policyContent = (
            <>
              <ContactSupportCard />
              <Button
                buttonType={ButtonTypeEnum.Secondary}
                className={cn("open-cancel-btn")}
                onClick={navigateToCancellation}
              >
                {copy.cancelFlightBtn}
              </Button>
            </>
          );
        } else {
          policyContent = (
            <Card className={cn("policy-card")}>
              <ChangeFlightEligibilityVoidable
                classes={{
                  contentActions: cn("content-actions"),
                  contentBody: cn("content-body"),
                  contentHeader: cn("content-header"),
                  modalContent: cn("modal-content", ["voidable"]),
                  goToExchangeBtn: cn("go-to-exchange-btn"),
                  openCancelBtn: cn("open-cancel-btn"),
                }}
                copy={copy}
                onContinueToExchange={handlers.goToExchange}
                onOpenFlightCancel={navigateToCancellation}
                policyCopy={context.policyCopy}
              />
            </Card>
          );
        }
        break;

      default:
        // Fallback: show contact support for any unexpected state
        policyContent = <ContactSupportCard />;
        break;
    }
  }

  return (
    <div className={clsx(block, className)}>
      {onBack && (
        <MyTripPageHeader
          className={cn("back-header")}
          onClick={onBack}
          linkText={t("selfServe.backToTripDetails")}
        />
      )}

      <div className={cn("content-wrapper")}>
        <div
          className={cn("two-column", {
            singleCol: !showRightColumn,
          })}
        >
          <div className={cn("left-column")}>{policyContent}</div>
          {showRightColumn && (
            <div className={cn("right-column")}>
              <Card className={cn("flight-details-card")}>
                <FlightDetailsPanel
                  itinerary={itinerary}
                  airportMap={airports}
                  airlineMap={airlines}
                  hideLayoverButton
                  stackCards
                />
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
